/**
 * Test data backing the Autogenerate button on the budget plan form.
 *
 * Fills every REQUIRED field so the primary action enables on the first try, and the optional
 * ones the form can offer a real value for. The GOAL SETTING is filled by the modal itself
 * rather than here, because it has to resolve against the live catalog — a generated id the
 * server does not hold would fail the "it must exist and be active" check and read as a bug
 * in the form.
 *
 * Keep it in lockstep with the form: a new required field must be added here in the same
 * change, or Autogenerate silently stops working.
 *
 * Values are synthetic and none of them lands in `rsys_pii`.
 */

const DESCRIPTIONS = [
  'Annual investment programme for the fiscal year, consolidated from every department.',
  'The year’s programmed investments, framed by the goals adopted in Goal Setting.',
  'Consolidated budget plan covering the regular and development programmes.',
];

const NOTES = [
  'Generated for testing. Replace with anything a reader of this plan should know.',
  'Draft frame only — the submission window and coverage dates are indicative.',
];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** `YYYY-MM-DD` — a calendar date, never an instant. */
function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * One budget plan, for a fiscal year a few ahead of this one.
 *
 * **The year is offset by a random 2–8 rather than being next year**, because one ACTIVE plan
 * per fiscal year is an invariant: without the spread, the second click would produce a year
 * the server refuses and Autogenerate would look broken. It is the same job the name suffixes
 * do on the catalog generators, adapted to a value that has to stay a plausible year.
 *
 * The dates follow the year they belong to: the window closes in the October BEFORE it, which
 * is the ordinary case and the reason the deadline is not held to the coverage start.
 */
export function sampleBudgetPlan(): {
  fiscalYear: number;
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
  description: string;
  notes: string;
} {
  const fiscalYear = new Date().getFullYear() + 2 + Math.floor(Math.random() * 7);

  return {
    fiscalYear,
    submissionClosesAt: isoDate(fiscalYear - 1, 10, 31),
    coverageStartDate: isoDate(fiscalYear, 1, 1),
    coverageEndDate: isoDate(fiscalYear, 12, 31),
    description: pick(DESCRIPTIONS),
    notes: pick(NOTES),
  };
}
